/**
 * Salary structure applicability: match employees + detect assignment overlap.
 * Assignment shape (from structure templates):
 * {
 *   allEmployees, branch, department, designation, grade, category,
 *   subDepartment, location, employmentTypes[], employeeIds[]
 * }
 */

function normId(value) {
  if (value == null || value === "") return "";
  return String(value);
}

function asIdList(value) {
  if (Array.isArray(value)) return value.map(normId).filter(Boolean);
  if (value == null || value === "") return [];
  return [normId(value)];
}

function asTypeList(value) {
  if (Array.isArray(value)) return value.map((v) => String(v)).filter(Boolean);
  if (value == null || value === "") return [];
  return [String(value)];
}

/**
 * Canonical employment-type key for matching.
 * Employee form uses "Full-time" / "External"; structure assignment uses "full_time" / "external".
 */
export function normalizeEmploymentTypeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function employmentTypesMatch(assignedTypes, employeeType) {
  if (!assignedTypes || !assignedTypes.length) return true; // empty = all types
  const empKey = normalizeEmploymentTypeKey(employeeType);
  if (!empKey) return false;
  return assignedTypes.some((t) => normalizeEmploymentTypeKey(t) === empKey);
}

function employmentTypeListsIntersect(a, b) {
  const setB = new Set((b || []).map(normalizeEmploymentTypeKey).filter(Boolean));
  return (a || []).some((x) => setB.has(normalizeEmploymentTypeKey(x)));
}

export function normalizeAssignment(assignment = {}) {
  return {
    allEmployees: !!assignment.allEmployees,
    branch: normId(assignment.branch),
    department: normId(assignment.department),
    designation: normId(assignment.designation),
    grade: normId(assignment.grade),
    category: normId(assignment.category),
    subDepartment: normId(assignment.subDepartment || assignment.subdepartment),
    location: normId(assignment.location),
    employmentTypes: asTypeList(
      assignment.employmentTypes || assignment.employmentType
    ),
    employeeIds: asIdList(assignment.employeeIds || assignment.employees),
  };
}

function dimMatch(assignedValue, employeeValue) {
  if (!assignedValue) return true; // empty = any
  return normId(assignedValue) === normId(employeeValue);
}

/** Specificity score — higher = more specific assignment. */
export function assignmentSpecificity(assignment = {}) {
  const a = normalizeAssignment(assignment);
  let score = 0;
  // allEmployees = all org units; employment types still add specificity
  if (a.allEmployees) {
    if (a.employmentTypes.length) score += 5;
    return score;
  }
  if (a.employeeIds.length) score += 100;
  if (a.branch) score += 10;
  if (a.department) score += 10;
  if (a.subDepartment) score += 8;
  if (a.designation) score += 10;
  if (a.grade) score += 8;
  if (a.category) score += 8;
  if (a.location) score += 5;
  if (a.employmentTypes.length) score += 5;
  return score;
}

/**
 * Does this structure assignment apply to the employee?
 * allEmployees = all org units (branch/dept/…); employment types still filter.
 */
export function structureAppliesToEmployee(assignment, employee = {}) {
  const a = normalizeAssignment(assignment);

  if (a.employmentTypes.length) {
    if (!employmentTypesMatch(a.employmentTypes, employee.employmentType)) return false;
  }

  if (a.allEmployees) return true;

  if (a.employeeIds.length) {
    const empId = normId(employee.id || employee.employeeId);
    if (empId && a.employeeIds.includes(empId)) return true;
  }

  const hasDim =
    a.branch ||
    a.department ||
    a.designation ||
    a.grade ||
    a.category ||
    a.subDepartment ||
    a.location ||
    a.employmentTypes.length > 0;

  if (a.employeeIds.length && !hasDim) {
    return a.employeeIds.includes(normId(employee.id || employee.employeeId));
  }

  if (!hasDim && !a.employeeIds.length) {
    // No scope configured — treat as unmatched (safer than matching everyone)
    return false;
  }

  if (a.employeeIds.length) {
    const inList = a.employeeIds.includes(normId(employee.id || employee.employeeId));
    if (inList) return true;
  }

  if (!dimMatch(a.branch, employee.branchId)) return false;
  if (!dimMatch(a.department, employee.departmentId)) return false;
  if (!dimMatch(a.designation, employee.designationId)) return false;
  if (!dimMatch(a.grade, employee.gradeId)) return false;
  if (!dimMatch(a.category, employee.categoryId)) return false;
  if (!dimMatch(a.subDepartment, employee.subDepartmentId)) return false;
  if (!dimMatch(a.location, employee.locationId || employee.workLocationId)) return false;

  return true;
}

/**
 * Flatten nested org relations so matching works for list rows and getEmployee payloads.
 */
export function normalizeEmployeeForStructureMatch(employee = {}) {
  if (!employee || typeof employee !== "object") return {};
  return {
    ...employee,
    id: employee.id ?? employee.employeeId,
    ctc: employee.ctc ?? employee.ctcAmount ?? employee.user?.ctc,
    branchId: employee.branchId ?? employee.branch?.id ?? "",
    departmentId: employee.departmentId ?? employee.department?.id ?? "",
    designationId: employee.designationId ?? employee.designation?.id ?? "",
    gradeId: employee.gradeId ?? employee.grade?.id ?? "",
    categoryId: employee.categoryId ?? employee.category?.id ?? "",
    subDepartmentId:
      employee.subDepartmentId ?? employee.subDepartment?.id ?? "",
    locationId:
      employee.locationId ??
      employee.workLocationId ??
      employee.location?.id ??
      employee.workLocation?.id ??
      "",
    workLocationId:
      employee.workLocationId ??
      employee.locationId ??
      employee.workLocation?.id ??
      employee.location?.id ??
      "",
    employmentType: employee.employmentType ?? "",
  };
}

/**
 * Find the single best applicable active structure for an employee.
 * Prefers higher specificity, then newer updatedAt/createdAt.
 */
export function findApplicableSalaryStructure(employee, structures = []) {
  const emp = normalizeEmployeeForStructureMatch(employee);
  const active = (structures || []).filter((s) => s && s.active !== false);
  const matches = active.filter((s) =>
    structureAppliesToEmployee(s.assignment || {}, emp)
  );
  if (!matches.length) return null;

  matches.sort((a, b) => {
    const spec =
      assignmentSpecificity(b.assignment) - assignmentSpecificity(a.assignment);
    if (spec !== 0) return spec;
    const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
    const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
    return tb - ta;
  });
  return matches[0];
}

function listsIntersect(a, b) {
  const setB = new Set(b.map(String));
  return a.some((x) => setB.has(String(x)));
}

/**
 * True if two assignments could cover the same employee group.
 *
 * - allEmployees = all org units (branch/dept/…), NOT all employment types.
 * - Empty employmentTypes = every employment type within that org scope.
 * - Disjoint employment-type lists never overlap (e.g. Full-time vs External).
 */
export function assignmentsOverlap(assignmentA, assignmentB) {
  const a = normalizeAssignment(assignmentA);
  const b = normalizeAssignment(assignmentB);

  // Employment types first — empty means "all types" (normalize Full-time ↔ full_time)
  if (a.employmentTypes.length && b.employmentTypes.length) {
    if (!employmentTypeListsIntersect(a.employmentTypes, b.employmentTypes)) return false;
  }

  if (a.employeeIds.length && b.employeeIds.length) {
    if (!listsIntersect(a.employeeIds, b.employeeIds)) {
      const aHasOrgDim =
        a.branch ||
        a.department ||
        a.designation ||
        a.grade ||
        a.category ||
        a.subDepartment ||
        a.location ||
        a.allEmployees;
      const bHasOrgDim =
        b.branch ||
        b.department ||
        b.designation ||
        b.grade ||
        b.category ||
        b.subDepartment ||
        b.location ||
        b.allEmployees;
      // Disjoint explicit lists and no org-wide scope on either side
      if (!aHasOrgDim && !bHasOrgDim) return false;
    } else {
      return true;
    }
  }

  // Org dimensions: allEmployees = unrestricted on these dims
  if (!a.allEmployees && !b.allEmployees) {
    const dims = [
      "branch",
      "department",
      "designation",
      "grade",
      "category",
      "subDepartment",
      "location",
    ];
    for (const dim of dims) {
      if (a[dim] && b[dim] && a[dim] !== b[dim]) return false;
    }
  }

  // Could not prove disjoint → treat as overlapping
  return true;
}

/**
 * Find an existing active structure that conflicts with the proposed assignment.
 * @returns {object|null} conflicting structure
 */
export function findConflictingSalaryStructure(
  proposedAssignment,
  structures = [],
  excludeId = null
) {
  const proposed = normalizeAssignment(proposedAssignment);
  for (const s of structures || []) {
    if (!s || s.active === false) continue;
    if (excludeId != null && String(s.id) === String(excludeId)) continue;
    if (assignmentsOverlap(proposed, s.assignment || {})) {
      return s;
    }
  }
  return null;
}
